import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { CheckIcon } from './CustomPlayerIcons';
import type { SettingsPanelType } from './CustomPlayer.types';
import styles from './CustomPlayerSettingsPanel.module.scss';

type Option = {
  label: string;
  value: string | number;
};

type Props = {
  activePanel: SettingsPanelType;
  qualityOptions: Option[];
  subtitleOptions: Option[];
  audioTrackOptions: Option[];
  playbackSpeedOptions: Option[];
  selectedQuality: string;
  selectedSubtitle: string;
  selectedAudioTrack: string;
  selectedPlaybackSpeed: number;
  onQualityChange: (value: string) => void;
  onSubtitleChange: (value: string) => void;
  onAudioTrackChange: (value: string) => void;
  onPlaybackSpeedChange: (value: number) => void;
  showQuality?: boolean;
  showSubtitles?: boolean;
  showAudioTracks?: boolean;
  showPlaybackSpeed?: boolean;
};

const SettingsList: React.FC<{
  title: string;
  options: Option[];
  selectedValue: string | number;
  onSelect: (value: string | number) => void;
}> = ({ title, options, selectedValue, onSelect }) => (
  <div className={styles.column}>
    <h3 className={styles.title}>{title}</h3>
    <ul className={styles.list}>
      {options.map((option, index) => (
        <li key={`${option.value}-${index}`}>
          <button
            type="button"
            className={classNames(styles.item, { [styles.selected]: option.value === selectedValue })}
            onClick={() => onSelect(option.value)}
            aria-current={option.value === selectedValue ? 'true' : undefined}
          >
            <span>{option.label}</span>
            {option.value === selectedValue && <CheckIcon className={styles.check} />}
          </button>
        </li>
      ))}
    </ul>
  </div>
);

const CustomPlayerSettingsPanel: React.FC<Props> = ({
  activePanel,
  qualityOptions,
  subtitleOptions,
  audioTrackOptions,
  playbackSpeedOptions,
  selectedQuality,
  selectedSubtitle,
  selectedAudioTrack,
  selectedPlaybackSpeed,
  onQualityChange,
  onSubtitleChange,
  onAudioTrackChange,
  onPlaybackSpeedChange,
  showQuality = true,
  showSubtitles = true,
  showAudioTracks = true,
  showPlaybackSpeed = true,
}) => {
  const { t } = useTranslation('player');
  const hasMultipleAudioTracks = showAudioTracks && audioTrackOptions.length > 1;

  const subtitleList = useMemo(() => {
    if (subtitleOptions.length) {
      return subtitleOptions;
    }

    return [{ label: t('off'), value: 'off' }];
  }, [subtitleOptions, t]);

  if (!activePanel || activePanel === 'volume') {
    return null;
  }

  if (activePanel === 'subtitles' && (showSubtitles || hasMultipleAudioTracks)) {
    if (hasMultipleAudioTracks) {
      return (
        <div className={classNames(styles.panelAnchor, styles.multiPanelAnchor)} data-player-popup>
          <div className={classNames(styles.panel, styles.multiPanel)}>
            <SettingsList
              title={t('audio_language')}
              options={audioTrackOptions}
              selectedValue={selectedAudioTrack}
              onSelect={(value) => onAudioTrackChange(String(value))}
            />
            <div className={styles.divider} />
            <SettingsList
              title={t('subtitles')}
              options={subtitleList}
              selectedValue={selectedSubtitle}
              onSelect={(value) => onSubtitleChange(String(value))}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={styles.panelAnchor} data-player-popup>
        <div className={styles.panel}>
          <SettingsList
            title={t('subtitles')}
            options={subtitleList}
            selectedValue={selectedSubtitle}
            onSelect={(value) => onSubtitleChange(String(value))}
          />
        </div>
      </div>
    );
  }

  const renderSinglePanel = () => {
    switch (activePanel) {
      case 'settings':
      case 'quality':
        if (!showQuality) return null;
        return (
          <SettingsList
            title={t('quality')}
            options={qualityOptions}
            selectedValue={selectedQuality}
            onSelect={(value) => onQualityChange(String(value))}
          />
        );
      case 'playbackSpeed':
        if (!showPlaybackSpeed) return null;
        return (
          <SettingsList
            title={t('playback_speed')}
            options={playbackSpeedOptions}
            selectedValue={selectedPlaybackSpeed}
            onSelect={(value) => onPlaybackSpeedChange(Number(value))}
          />
        );
      default:
        return null;
    }
  };

  const content = renderSinglePanel();
  if (!content) {
    return null;
  }

  return (
    <div className={styles.panelAnchor} data-player-popup>
      <div className={styles.panel}>{content}</div>
    </div>
  );
};

export default CustomPlayerSettingsPanel;
